import type { UnknownAction } from "@reduxjs/toolkit";
import type { TFunction } from "i18next";

import type { IAdvisorStarter } from "@/components/ai-assistant/advisor";
import { advisorStarters as companyStarters } from "@/components/ai-assistant/advisor/company-starters.data";
import { advisorStarters as mySpaceStarters } from "@/components/ai-assistant/advisor/my-space-starters.data";
import type { ConversationHistoryApi } from "@/components/ai-assistant/conversation-history";
import { APIEndpoints } from "@/constants/apiEndpoints";
import { COMPANY_AI_ASSISTANT_PATHS } from "@/routes/company/routes.ai-assistant";
import { MS_AI_ASSISTANT_PATHS } from "@/routes/my-space/routes.ai-assistant";
import * as companyAi from "@/services/(company)/api.ai-assistant";
import * as mySpaceAi from "@/services/(my-space)/api.ai-assistant";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import type {
  IChatSessionData,
  IConversationHistory,
} from "@/types/(my-space)/ai-assistant.interface";

import { STORAGE_KEYS } from "./constants";

/**
 * The space Maya's sidebar works in, following the page beside it: My Space
 * (personal chats) or Workspace (company chats, which need the Workspace AI
 * Assistant permission). The server keeps the two chat lists apart, and each
 * space's full AI Assistant page lists only its own.
 */
export type MayaSpace = "my-space" | "company";

/** An RTK Query trigger, as the sidebar calls it. */
type Trigger<Arg, Result> = (arg: Arg) => { unwrap: () => Promise<Result> };

export interface MayaSpaceConfig {
  space: MayaSpace;
  /** Its name, shown in the sidebar header (common namespace). */
  labelKey: string;
  /** Where its turns are sent, re-answered and their files uploaded. */
  endpoints: { chat: string; retry: string; upload: string };
  /** Its full AI Assistant page, on the chat when there is one. */
  fullPage: (sessionId: string | null) => string;
  /** Advisor choice is remembered per space (see useAdvisor). */
  advisorScope: MayaSpace;
  /** Advisor copy phrased for one person rather than the company. */
  personal: boolean;
  starters: (slug: string, t: TFunction) => IAdvisorStarter[];
  /** Its open chat and unsent draft, kept apart from the other space's. */
  storage: { chat: string; draft: string };
  /** The chat list, with pin, rename, share and delete — its page's list. */
  history: ConversationHistoryApi;
  useGetConversation: () => readonly [
    Trigger<
      { sessionId: string; messageId?: number; page?: number; limit?: number },
      IApiPaginatedResponse<IConversationHistory>
    >,
    ...unknown[],
  ];
  useEditMessage: () => readonly [
    Trigger<
      { messageId: number; content: string },
      IApiResponse<IChatSessionData>
    >,
    ...unknown[],
  ];
  useSpeechToText: () => readonly [
    Trigger<FormData, IApiResponse<{ output: string }>>,
    ...unknown[],
  ];
  /** Refetch its chat list (a new chat, a new title). */
  invalidateChats: () => UnknownAction;
}

/**
 * Picked once per space: the sidebar's chat remounts when the space changes
 * (see MayaSidebar), so each mount calls one space's hooks throughout.
 */
export const MAYA_SPACES: Record<MayaSpace, MayaSpaceConfig> = {
  "my-space": {
    space: "my-space",
    labelKey: "common:spaces.my_space",
    endpoints: {
      chat: APIEndpoints.MySpace.AIAssistant.SmartChat,
      retry: APIEndpoints.MySpace.AIAssistant.RetryMessage,
      upload: APIEndpoints.MySpace.AIAssistant.MayaFileUpload,
    },
    fullPage: (sessionId) =>
      sessionId
        ? MS_AI_ASSISTANT_PATHS.CHAT_SESSION(sessionId)
        : MS_AI_ASSISTANT_PATHS.ROOT,
    advisorScope: "my-space",
    personal: true,
    starters: mySpaceStarters,
    // The original keys, so a chat open before spaces existed carries on.
    storage: { chat: STORAGE_KEYS.chat, draft: STORAGE_KEYS.draft },
    history: {
      useConversations: mySpaceAi.useGetConversationHistoryInfiniteQuery,
      useQuestionsBySession: mySpaceAi.useGetQuestionsBySessionIdInfiniteQuery,
      usePinConversation: mySpaceAi.usePinConversationMutation,
      useRenameConversation: mySpaceAi.useRenameConversationMutation,
      useDeleteConversation: mySpaceAi.useDeleteConversationMutation,
      useLazyGetShareLink: mySpaceAi.useLazyGetShareLinkQuery,
      useCreateShareLink: mySpaceAi.useCreateShareLinkMutation,
      useRevokeShareLink: mySpaceAi.useRevokeShareLinkMutation,
    },
    useGetConversation: mySpaceAi.useLazyGetSingleConversationQuery,
    useEditMessage: mySpaceAi.useEditMessageMutation,
    useSpeechToText: mySpaceAi.useSpeechToTextMutation,
    invalidateChats: () =>
      mySpaceAi.aiAssistantApi.util.invalidateTags(["GetAllConversations"]),
  },
  company: {
    space: "company",
    labelKey: "common:spaces.company",
    endpoints: {
      chat: APIEndpoints.Company.AIAssistant.SmartChat,
      retry: APIEndpoints.Company.AIAssistant.RetryMessage,
      upload: APIEndpoints.Company.AIAssistant.MayaFileUpload,
    },
    fullPage: (sessionId) =>
      sessionId
        ? COMPANY_AI_ASSISTANT_PATHS.CHAT_SESSION(sessionId)
        : COMPANY_AI_ASSISTANT_PATHS.ROOT,
    advisorScope: "company",
    personal: false,
    starters: companyStarters,
    storage: {
      chat: STORAGE_KEYS.companyChat,
      draft: STORAGE_KEYS.companyDraft,
    },
    history: {
      useConversations: companyAi.useGetConversationHistoryInfiniteQuery,
      useQuestionsBySession: companyAi.useGetQuestionsBySessionIdInfiniteQuery,
      usePinConversation: companyAi.usePinConversationMutation,
      useRenameConversation: companyAi.useRenameConversationMutation,
      useDeleteConversation: companyAi.useDeleteConversationMutation,
      useLazyGetShareLink: companyAi.useLazyGetShareLinkQuery,
      useCreateShareLink: companyAi.useCreateShareLinkMutation,
      useRevokeShareLink: companyAi.useRevokeShareLinkMutation,
    },
    useGetConversation: companyAi.useLazyGetSingleConversationQuery,
    useEditMessage: companyAi.useEditMessageMutation,
    useSpeechToText: companyAi.useSpeechToTextMutation,
    invalidateChats: () =>
      companyAi.companyAiAssistantApi.util.invalidateTags([
        "CompanyGetAllConversations",
      ]),
  },
};
